import { useComponentTranslation } from "@/services/i18n";

const translations = {
  en: {
    yes: "Yes",
    no: "No",
    select_option: "Select an option",
    enter_answer: "Enter your answer",
  },
  fr: {
    yes: "Oui",
    no: "Non",
    select_option: "Sélectionner une option",
    enter_answer: "Entrez votre réponse",
  },
};

export default function CustomQuestionsField({ questions, values, setValues, showValidation }) {
  const { t } = useComponentTranslation(translations);
  
  if (!questions?.length) return null;

  const getAnswerKey = (questionId) => `custom_${questionId}`;

  const handleChange = (questionId, value) => {
    setValues({ ...values, [getAnswerKey(questionId)]: value });
  };

  const isFieldEmpty = (value) => !value || (typeof value === "string" && value.trim() === "");

  return (
    <div className="space-y-4">
      {questions.map((question) => {
        const answerKey = getAnswerKey(question.id);
        const value = values[answerKey] || "";
        const isEmpty = isFieldEmpty(value);
        const showError = showValidation && question.required && isEmpty;

        return (
          <div key={question.id} className="flex flex-col">
            <label htmlFor={answerKey} className="input-label">
              {question.question} {question.required && "*"}
            </label>

            {/* Short answer - single line text input */}
            {question.type === "short" && (
              <input
                type="text"
                id={answerKey}
                name={answerKey}
                value={value}
                onChange={(e) => handleChange(question.id, e.target.value)}
                className={`input ${showError ? "border-red-500" : ""}`}
                placeholder={t("enter_answer")}
              />
            )}

            {/* Long answer - textarea */}
            {question.type === "long" && (
              <textarea
                id={answerKey}
                name={answerKey}
                value={value}
                onChange={(e) => handleChange(question.id, e.target.value)}
                rows={4}
                className={`input ${showError ? "border-red-500" : ""}`}
                placeholder={t("enter_answer")}
              />
            )}

            {/* Yes/No - radio buttons */}
            {question.type === "yes_no" && (
              <div className="flex gap-6 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name={answerKey}
                    value="yes"
                    checked={value === "yes"}
                    onChange={(e) => handleChange(question.id, e.target.value)}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{t("yes")}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name={answerKey}
                    value="no"
                    checked={value === "no"}
                    onChange={(e) => handleChange(question.id, e.target.value)}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{t("no")}</span>
                </label>
              </div>
            )}

            {/* Multiple choice - select dropdown */}
            {question.type === "multiple_choice" && question.options?.length > 0 && (
              <select
                id={answerKey}
                name={answerKey}
                value={value}
                onChange={(e) => handleChange(question.id, e.target.value)}
                className={`input ${showError ? "border-red-500" : ""}`}
              >
                <option value="">{t("select_option")}</option>
                {question.options.map((option, idx) => (
                  <option key={idx} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            )}

            {/* Validation error indicator */}
            {showError && (
              <p className="text-red-500 text-xs mt-1">This field is required</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
